import React, { useState, useMemo } from 'react'
import { useFloating, inline, offset, flip, shift } from '@floating-ui/react-dom'
import Styles from './OnlineFollowersHeatmap.module.scss'
import { MetricContainer } from '../MetricContainer/MetricContainer'
import { Text } from 'blaze-ui/Text/Text'
import dayjs from 'dayjs'
import { OnlineFollowersData } from 'hooks/queries/audienceMetrics/types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Spin } from 'common/Spin/Spin'

const formatHour = (hour: number): string => {
  if (hour === 0) return '12AM'
  if (hour === 12) return '12PM'
  return `${hour % 12}${hour < 12 ? 'AM' : 'PM'}`
}

const formatTimeRange = (hour: number): string => {
  const startHour = formatHour(hour)
  const endHour = formatHour((hour + 1) % 24)
  return `${startHour} to ${endHour}`
}

const isDataEmpty = (data: OnlineFollowersData): boolean => {
  // Get all days
  const days = Object.values(data.days)

  // Check each day
  for (const day of days) {
    // Get all hours for the day
    const hours = Object.values(day.hours)

    // Check if any hour has a non-zero value
    const hasNonZeroValue = hours.some((value) => value !== 0)

    // If we found any non-zero value, the schedule is not empty
    if (hasNonZeroValue) {
      return false
    }
  }

  // If we've checked all days and hours and found no non-zero values,
  // the schedule is empty
  return true
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const OnlineFollowersHeatmap = ({ data, isFetching }: { data?: OnlineFollowersData; isFetching: boolean }) => {
  const [hoveredCell, setHoveredCell] = useState<{
    day: string
    hour: number
    value: number
    date: Date
  } | null>(null)

  const { isMobile } = useResponsiveDesign()

  const { x, y, reference, floating, strategy } = useFloating({
    placement: 'top',
    middleware: [inline(), offset(8), flip(), shift({ padding: 4 })],
  })

  const isEmpty = useMemo(() => data && isDataEmpty(data), [data])

  const { maxValue, minValue } = useMemo(() => {
    const values = Object.values(data?.days || {}).flatMap((day) => Object.values(day.hours))
    return {
      maxValue: Math.max(...values),
      minValue: Math.min(...values),
    }
  }, [data])

  const getColorIntensity = (value: number) => {
    const normalized = (value - minValue) / (maxValue - minValue)
    // Sigmoid function: 1 / (1 + e^(-x))
    // Adjusting input range to be centered around 0 and scaled for steepness
    const intensity = 1 / (1 + Math.exp(-5 * (normalized - 0.5)))
    return `rgba(59, 130, 246, ${Math.max(0.1, intensity)})`
  }

  const handleCellHover = (element: HTMLDivElement | null, day: string, hour: number, value: number) => {
    reference(element)
    if (element) {
      // Get the current week's date for the given day
      const currentDate = dayjs()
      const dayIndex = DAYS.findIndex((d) => d.toLowerCase() === day.toLowerCase())
      const date = currentDate.day(dayIndex).toDate()

      setHoveredCell({ day, hour, value, date })
    } else {
      setHoveredCell(null)
    }
  }

  let content

  if (isFetching) {
    content = (
      <div className={Styles.loadingContainer} data-testid="loading">
        <Spin />
      </div>
    )
  } else if (!data) {
    content = <div className={Styles.loadingContainer}>Error loading data</div>
  } else if (isEmpty) {
    content = <div className={Styles.loadingContainer}>This data isn't ready yet, check back soon!</div>
  } else {
    content = (
      <div className={Styles.grid}>
        {/* Time labels */}
        <div className={Styles.timeLabels}>
          {Array.from({ length: 24 }, (_, i) => (
            <div key={i} className={Styles.timeLabel}>
              {formatHour(i)}
            </div>
          ))}
        </div>

        {/* Heatmap grid */}
        <div className={Styles.heatmapGrid}>
          {DAYS.map((day) => (
            <div key={day} className={Styles.dayColumn}>
              <div className={Styles.dayLabel}>{isMobile ? day.slice(0, 3) : day}</div>
              <div className={Styles.cells}>
                {Array.from({ length: 24 }, (_, hour) => {
                  const value = data.days[day.toLowerCase() as keyof OnlineFollowersData['days']].hours[hour]
                  return (
                    <div
                      key={hour}
                      className={Styles.cell}
                      style={{ backgroundColor: getColorIntensity(value) }}
                      onMouseEnter={(e) => handleCellHover(e.currentTarget, day, hour, value)}
                      onMouseLeave={() => handleCellHover(null, day, hour, value)}
                    />
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <MetricContainer name="Online Followers" tooltip="The number of followers online at a given time">
      {content}
      {hoveredCell && (
        <div
          ref={floating}
          className={Styles.tooltip}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
          }}
        >
          <div className={Styles.tooltipSection}>
            <Text variant="smallList" color="var(--dark-80)">
              {dayjs(hoveredCell.date).format('ddd, MMM D')}
            </Text>
            <Text variant="metadata" color="var(--dark-60)">
              {formatTimeRange(hoveredCell.hour)}
            </Text>
          </div>
          <div className={Styles.tooltipSection}>
            <Text variant="smallList" color="var(--dark-80)">
              {hoveredCell.value}
            </Text>
            <Text variant="metadata" color="var(--dark-60)">
              Followers online
            </Text>
          </div>
        </div>
      )}
    </MetricContainer>
  )
}

export default OnlineFollowersHeatmap
